/**
 * domainsVmBuilder — traduz a resposta de `GET /v1/domains/status` no ViewModel do contrato @v3.
 *
 * É uma função PURA de propósito: é a peça que decide o que a tela mostra, e precisa ser testável
 * sem montar React. O `useDomains` cuida de estado, efeitos e ações; tudo que é tradução mora
 * aqui.
 *
 * Contrato: `domains.vm.js` (@version 3). Toda string que chega na View sai formatada daqui — a
 * View nunca calcula, nunca deriva, nunca formata.
 */
import { formatCurrency, formatDate, formatRelativeDistance } from '../../utils/formatters';

/** Passo parado mais que isso não está demorando — está travado, e a tela precisa admitir. */
export const HORAS_ATE_CONSIDERAR_TRAVADO = 24;
/** Aviso de renovação começa com esta antecedência (é quando a cobrança de renovação é gerada). */
export const DIAS_AVISO_RENOVACAO = 30;

// Os inteiros vêm do PageCatalog (1 link na bio, 2 profissional, 3 empresa) — mesmo enum do
// backend, não inventar aqui.
const TIPO_LABEL = { 1: 'Link na bio', 2: 'Página do profissional', 3: 'Página da empresa' };

/**
 * Passo do registro, do jeito que o cliente entende.
 *
 * O servidor fala em status de banco (`paid`, `registered`, `provisioning`, `active`, `failed`,
 * `expired` — vocabulário travado pela constraint `page_domain_status_chk`); aqui vira o que a
 * pessoa quer saber: em que etapa está e quanto ainda demora. As esperas são honestas de
 * propósito — quem acha que travou abre chamado.
 */
export const PASSO = {
  // Registro feito à mão por enquanto (09/10/2026): a espera prometida é a real, não a do automático.
  paid: { step: 'registering', stepLabel: 'Registrando o domínio…', etaLabel: 'pode levar até 1 dia útil', progressPercent: 25 },
  registered: { step: 'pointing', stepLabel: 'Apontando os servidores de nome…', etaLabel: 'pode levar algumas horas', progressPercent: 50 },
  provisioning: { step: 'securing', stepLabel: 'Emitindo o certificado de segurança…', etaLabel: 'faltam poucos minutos', progressPercent: 75 },
  active: { step: 'active', stepLabel: 'Seu domínio está no ar', etaLabel: '', progressPercent: 100 },
  failed: { step: 'failed', stepLabel: 'Não conseguimos concluir o registro', etaLabel: '', progressPercent: 25 },
  expired: { step: 'expired', stepLabel: 'Este domínio venceu', etaLabel: '', progressPercent: 100 },
};

/** Passos finais: não há mais o que esperar, então não faz sentido falar em "travado". */
const PASSOS_FINAIS = ['active', 'failed', 'expired'];

/**
 * Estados em que o card não aparece: a venda não está liberada para este usuário, ou não há nada
 * a mostrar nem a ofertar.
 *
 * A View também se protege (retorna null), mas quem EMBUTE precisa saber disso **antes** de
 * montar a moldura — senão sobra um espaço vazio no meio da Home.
 */
export const ESTADOS_SEM_CARD = ['hidden', 'empty'];

/**
 * Estados que aparecem quando o card está EMBUTIDO em outra tela (Home, lista de Páginas).
 *
 * ⚠️ Embutido ≠ tela cheia. A Home é "visão geral das aulas"; ela não é o lugar de administrar
 * domínio.
 *
 * ⚠️ **REMOVIDA em 09/09/2026, e não deve voltar.** Ela era o filtro do modo `embedded`, que
 * punha ESTA tela dentro da Home. A regra do André (08/09) proíbe:
 *
 *   "cards e afins na home JAMAIS são partes/partials de página de menu. Nada se compartilha
 *    de front entre, por exemplo, card de domínio da home e página de domínio."
 *
 * A Home hoje tem `domainOffer` e `domainPayment` PRÓPRIOS, desenhados dentro da DashboardView
 * e alimentados por `GET /v1/home`. Reintroduzir um "modo embutido" aqui é reabrir o caminho que
 * transformou a Home num painel de domínios.
 */

/** Estados em que reapontar a raiz faz sentido: o domínio já é nosso e ainda vale. */
const ACEITA_TROCA_DE_RAIZ = ['registered', 'provisioning', 'active'];

/** Os quatro passos do apontamento, na ordem, com o rótulo que o cliente lê. */
export const TIMELINE = [
  { key: 'register', label: 'Domínio registrado' },
  { key: 'nameservers', label: 'Servidores de nome apontados' },
  { key: 'hostname', label: 'Endereço configurado' },
  { key: 'certificate', label: 'Certificado de segurança emitido' },
];

/** Até onde a linha do tempo está concluída, a partir do `dns_status` REAL do banco. */
const PASSOS_CONCLUIDOS_POR_DNS = { pending: 1, nameservers_set: 2, hostname_added: 3, active: 4 };

export const MOTIVO_LABEL = {
  taken: 'Esse já tem dono.',
  premium: 'Esse é um nome premium e sai muito acima do nosso plano.',
  too_expensive: 'Esse nome custa bem mais do que o nosso plano cobre.',
  invalid_domain: 'Use só letras, números e hífen — sem espaços nem acento.',
};

export const ERRO_COMPRA_LABEL = {
  purchase_already_started: 'Você já tem uma compra em andamento — ela está aqui embaixo.',
  domain_already_yours: 'Esse domínio já é seu.',
  domain_not_available: 'Esse domínio deixou de estar disponível. Escolha outro.',
  root_page_not_yours: 'Escolha uma das suas páginas publicadas para responder na raiz.',
  invalid_domain: 'Use só letras, números e hífen — sem espaços nem acento.',
  product_not_configured: 'A compra está indisponível no momento. Tente mais tarde.',
  availability_unavailable: 'Não conseguimos conferir o domínio agora. Tente de novo.',
};

const BENEFICIOS = [
  { key: 'brand', title: 'O endereço é seu', description: 'sorrisoreal.com em vez de um endereço dentro do nosso site.' },
  { key: 'trust', title: 'Passa mais confiança', description: 'Quem recebe o link vê o nome do seu negócio, não o de outra empresa.' },
  { key: 'memory', title: 'Fácil de lembrar e de ditar', description: 'Cabe no cartão, na placa e numa conversa por telefone.' },
  { key: 'ownership', title: 'Continua seu', description: 'O registro fica no seu nome. Você leva para onde quiser.' },
];

const reais = (cents) => formatCurrency((cents ?? 0) / 100);

// ── Peças ────────────────────────────────────────────────────────────────────

/** Quanto falta para a cobrança vencer, em texto + urgência para a View enfatizar. */
export function rotularExpiracao(iso, agora = Date.now()) {
  if (!iso) return { expiresLabel: 'válida por 24 horas', urgency: 'normal', expired: false };
  const restaMs = new Date(iso).getTime() - agora;
  if (Number.isNaN(restaMs)) return { expiresLabel: 'válida por 24 horas', urgency: 'normal', expired: false };
  if (restaMs <= 0) return { expiresLabel: 'a cobrança expirou', urgency: 'expired', expired: true };

  const minutos = Math.floor(restaMs / 60000);
  if (minutos < 60) return { expiresLabel: `expira em ${minutos} min`, urgency: 'soon', expired: false };
  const horas = Math.round(minutos / 60);
  return { expiresLabel: `expira em ${horas} ${horas === 1 ? 'hora' : 'horas'}`, urgency: 'normal', expired: false };
}

/** Aviso de renovação. null enquanto o domínio não foi registrado (não há data ainda). */
export function montarRenovacao(expiresAt, vencido, agora = Date.now()) {
  if (!expiresAt) return null;
  const data = new Date(expiresAt);
  if (Number.isNaN(data.getTime())) return null;

  const dias = Math.round((data.getTime() - agora) / 86400000);
  if (vencido || dias < 0) {
    return { urgency: 'expired', label: `Venceu em ${formatDate(expiresAt)}`, detailLabel: formatRelativeDistance(expiresAt) };
  }
  return {
    urgency: dias <= DIAS_AVISO_RENOVACAO ? 'soon' : 'ok',
    label: `Renova em ${formatDate(expiresAt)}`,
    detailLabel: dias === 0 ? 'vence hoje' : `faltam ${dias} ${dias === 1 ? 'dia' : 'dias'}`,
  };
}

/** A linha do tempo do apontamento, a partir do status do domínio e do `dns_status`. */
export function montarTimeline(status, dnsStatus) {
  if (status === 'failed') {
    // Falhou no registro: o primeiro passo é o que quebrou, os demais nem começaram.
    return TIMELINE.map((p, i) => ({ ...p, state: i === 0 ? 'failed' : 'todo' }));
  }
  // Antes de 'registered' nem o primeiro passo terminou. Depois, quem manda é o dns_status —
  // que é o progresso REAL, não uma estimativa de tempo.
  const concluidos = status === 'paid' ? 0
    : ['active', 'expired'].includes(status) ? TIMELINE.length
    : (PASSOS_CONCLUIDOS_POR_DNS[dnsStatus] ?? 1);

  return TIMELINE.map((p, i) => ({
    ...p,
    state: i < concluidos ? 'done' : i === concluidos ? 'current' : 'todo',
  }));
}

/** "https://sorrisoreal.com/dra-ana" — só vira link quando o domínio já responde. */
export function montarUrls(dominio, paginas, noAr, copiado) {
  const marcar = (label, pageTitle, isRoot) => ({
    label, pageTitle, isRoot,
    href: noAr ? `https://${label}` : null,
    copied: copiado === label,
  });
  const raiz = marcar(dominio.domain, dominio.rootPage?.title || '', true);
  const demais = (paginas || [])
    .filter((p) => p.pageId !== dominio.rootPage?.id)
    .map((p) => marcar(`${dominio.domain}/${p.slug}`, p.title, false));
  return [raiz, ...demais];
}

/** Uma página da conta com o endereço de hoje e como ela fica com o domínio novo. */
function montarPagina(p, dominioEscolhido, rootPageId) {
  const ehRaiz = p.pageId === rootPageId;
  return {
    pageId: p.pageId,
    title: p.title,
    typeLabel: TIPO_LABEL[p.type] ?? 'Página',
    currentUrlLabel: `pages.agendario.com.br/${p.slug}`,
    // O "vai ficar assim" só existe depois que ele escolhe um nome — é isso que torna a troca
    // concreta. Sem nome escolhido, mostra o formato.
    futureUrlLabel: dominioEscolhido
      ? (ehRaiz ? dominioEscolhido : `${dominioEscolhido}/${p.slug}`)
      : (ehRaiz ? 'seu domínio' : `seu domínio/${p.slug}`),
    isRoot: ehRaiz,
  };
}

// ── O ViewModel ──────────────────────────────────────────────────────────────

/**
 * @param {Object} entrada
 * @param {any} entrada.status  resposta de `GET /v1/domains/status`; `undefined` = ainda não
 *                              buscamos, `null` = 204 (venda não liberada para este usuário)
 * @returns {import('./domains.vm.js').DomainsVm}
 */
export function montarVm({
  status, carregando, errorMessage, actionFeedback, busy, refreshing, dispensado, lidoEm,
  selectedDomain, rootPageId, custom, copiado, cpfDialog, cancelDialog, rootDialog,
  agora = Date.now(),
}) {
  const priceCents = status?.priceCents ?? 0;
  const priceLabel = reais(priceCents);
  const periodLabel = 'por ano';
  const paginas = status?.pages ?? [];
  const aberta = status?.openPurchase ?? null;

  const state = (() => {
    if (carregando && status === undefined) return 'loading';
    if (errorMessage && status === undefined) return 'error';
    if (dispensado || status === null) return 'hidden';
    if (!status) return 'error';
    // Sem página publicada não há para onde apontar um domínio. Isso é oportunidade (convidar a
    // publicar), não vazio — por isso ganha estado próprio em vez de sumir com o card.
    if (status.state === 'empty' && paginas.length === 0) return 'needs_page';
    return status.state;
  })();

  const headline = (() => {
    switch (state) {
      case 'awaiting_payment':
        return { title: 'Pagamento em andamento', subtitle: `Estamos aguardando o PIX de ${aberta?.domain ?? 'sua compra'}.` };
      case 'owned':
        return { title: 'Seus domínios', subtitle: 'Acompanhe o que já é seu e para onde cada endereço aponta.' };
      case 'needs_page':
        return { title: 'Publique uma página primeiro', subtitle: 'Um domínio precisa de uma página para responder por ele.' };
      default:
        return { title: 'Tenha seu próprio endereço', subtitle: 'Um domínio .com no nome do seu negócio, apontando para as suas páginas.' };
    }
  })();

  const sugestoes = status?.suggestions ?? [];
  const paginasDaOferta = paginas.map((p) => montarPagina(p, selectedDomain, rootPageId));

  const offer = status?.canOffer
    ? {
        suggestions: sugestoes.map((s) => ({
          domain: s.domain,
          rank: s.rank,
          recommended: s.rank === 1,
          badgeLabel: s.rank === 1 ? 'Mais parecido com o seu negócio' : null,
          selected: s.domain === selectedDomain,
        })),
        suggestionsEmpty: sugestoes.length === 0,
        suggestionsEmptyLabel: 'Ainda estamos escolhendo nomes para o seu negócio. Enquanto isso, você pode buscar o seu.',
        custom: {
          ...custom,
          selected: Boolean(custom.input) && custom.input === selectedDomain,
          helperLabel: 'Só .com, sem acento e sem espaço.',
        },
        pages: paginasDaOferta,
        rootPageId,
        // Escolher entre uma opção só é ruído.
        showRootPicker: paginasDaOferta.length > 1,
        selectedDomain,
        canPurchase: Boolean(selectedDomain && rootPageId) && !busy,
        ctaLabel: `Comprar por ${priceLabel} ${periodLabel}`,
      }
    : null;

  const expiracao = rotularExpiracao(aberta?.pix?.expiresAt, agora);
  const payment = aberta
    ? {
        purchaseId: aberta.id,
        domain: aberta.domain,
        // O preço DESTA compra, não o do catálogo: o catálogo pode ter mudado desde que ela começou.
        priceLabel: reais(aberta.priceCents),
        rootPageLabel: paginas.find((p) => p.pageId === aberta.rootPageId)?.title ?? '',
        startedLabel: aberta.createdAt ? `gerado ${formatRelativeDistance(aberta.createdAt)}` : '',
        qrCodeImage: aberta.pix?.qrBase64 ? `data:image/png;base64,${aberta.pix.qrBase64}` : '',
        copyPasteCode: aberta.pix?.payload ?? '',
        expiresLabel: expiracao.expiresLabel,
        urgency: expiracao.urgency,
        expired: expiracao.expired,
        // A compra existe mas o código não veio: só resta desistir e comprar de novo. Melhor
        // dizer isso do que mostrar um quadrado branco onde deveria estar o QR.
        unavailable: !aberta.pix?.payload && !aberta.pix?.qrBase64,
        copied: copiado === 'pix',
        checking: refreshing,
        canCancel: !busy,
        reassuranceLabel: 'Assim que o banco confirmar, seguimos sozinhos — pode fechar esta tela.',
        nextStepLabel: 'Depois do pagamento, cuidamos do registro — pode levar até 1 dia útil.',
      }
    : null;

  const domains = (status?.domains ?? []).map((d) => {
    // 🔒 Falha de registro nunca chega ao cliente (decisão do André, 09/10/2026): ele pagou, e a
    // nós cabe registrar à mão ou estornar. A API já manda 'failed' como 'paid'; isto aqui só
    // garante o mesmo se uma API antiga ainda estiver no ar.
    const statusVisivel = d.status === 'failed' ? 'paid' : d.status;
    const passo = PASSO[statusVisivel] ?? PASSO.paid;
    const noAr = passo.step === 'active';
    const paradoHaMs = d.updatedAt ? agora - new Date(d.updatedAt).getTime() : 0;
    // O registro é feito à mão (prazo prometido: até 1 dia útil), então "parado" no primeiro
    // passo não é sinal de nada para o cliente — só alarmaria quem já pagou.
    const travado = !PASSOS_FINAIS.includes(passo.step) && passo.step !== 'registering'
      && paradoHaMs > HORAS_ATE_CONSIDERAR_TRAVADO * 3600000;

    return {
      id: d.id,
      domain: d.domain,
      ...passo,
      timeline: montarTimeline(statusVisivel, d.dnsStatus),
      stalled: travado,
      stalledLabel: travado
        ? `Está parado neste passo ${formatRelativeDistance(d.updatedAt)}. Já estamos verificando.`
        : null,
      urls: montarUrls(d, paginas, noAr, copiado),
      liveUrl: noAr ? `https://${d.domain}` : null,
      rootPageTitle: d.rootPage?.title ?? '',
      canChangeRootPage: ACEITA_TROCA_DE_RAIZ.includes(d.status) && !busy,
      renewal: montarRenovacao(d.expiresAt, d.status === 'expired', agora),
      registeredAtLabel: d.registeredAt ? `registrado em ${formatDate(d.registeredAt)}` : null,
      pricePaidLabel: reais(d.pricePaidCents),
      errorMessage: null,
      supportHint: null,
    };
  });

  const alvoDaTroca = (status?.domains ?? []).find((d) => d.id === rootDialog.domainId);

  return {
    state,
    headline,
    errorMessage,
    actionFeedback,
    busy,
    refreshing,
    // Sumir com o card no meio de uma cobrança é como o cliente se perdeu da primeira vez.
    dismissible: !aberta,
    lastUpdatedLabel: lidoEm ? `atualizado ${formatRelativeDistance(new Date(lidoEm))}` : '',

    price: {
      label: priceLabel,
      periodLabel,
      monthlyEquivalentLabel: `sai por ${reais(Math.round(priceCents / 12))} por mês`,
      renewalNoticeLabel: `Renova por ${priceLabel} daqui a 1 ano. Você decide se continua.`,
    },
    benefits: BENEFICIOS,

    offer,
    payment,
    domains,

    cpfDialog: {
      ...cpfDialog,
      explanationLabel: 'O PIX exige o CPF do titular. Pedimos uma vez só — nas próximas compras já fica salvo.',
    },
    cancelDialog: {
      ...cancelDialog,
      domain: aberta?.domain ?? '',
      warningLabel: 'Se você já pagou, não cancele: a confirmação pode levar alguns minutos.',
    },
    rootPageDialog: {
      open: rootDialog.open,
      busy: rootDialog.busy,
      domain: alvoDaTroca?.domain ?? '',
      // Fechado não monta lista: só gastaria trabalho e divergiria do mock (que traz []).
      // Aberto, o domínio já existe — então o preview é o endereço final de verdade.
      pages: rootDialog.open
        ? paginas.map((p) => montarPagina(p, alvoDaTroca?.domain ?? '', rootDialog.selectedPageId))
        : [],
      selectedPageId: rootDialog.selectedPageId,
      canConfirm: Boolean(rootDialog.selectedPageId)
        && rootDialog.selectedPageId !== alvoDaTroca?.rootPage?.id
        && !rootDialog.busy,
      error: rootDialog.error,
    },
  };
}
